/* State */
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useEffect } from "react";
import _ from "lodash";
import { fetchFlags } from "../../state/slices/featureflags/featureflags";
import {useCoreEntityContext} from "../useCoreEntitySlice";


const useFeatureFlag = (featurePath) => {
  const dispatch = useDispatch();
  const useCoreEntitySlice = useCoreEntityContext();

  const facilityID = useSelector(
	(state) =>useCoreEntitySlice ? state.coreEntities?.ContextID :  state.entities?.ContextID,
	shallowEqual
  );
  const entityFlag = !!useSelector(
    state => state.featureFlags?.flags?.includes(featurePath)
  );

	const needsReload = useSelector(
		state => !state.featureFlags?.isLoading && !state.featureFlags
	);


	useEffect(() => {
		if (needsReload) {
			dispatch(fetchFlags(facilityID));
		}
	}, [needsReload])

  return entityFlag;
}

/**
 * Checks a feature flag for an explicitly-supplied entity, rather than the current
 * entity context. This is required on public/unauthenticated pages such as the
 * customer-facing Ticket Summary Portal (mobilepay/{ticketId}), where the visitor
 * has no entity context set (state.coreEntities/entities ContextID is undefined),
 * so the context-based useFeatureFlag can never resolve the entity's flag list and
 * always returns false. Passing the ticket's facility id here fetches that
 * entity's flags (which include enabled global features such as "Receipt Lookup").
 *
 * The flags are fetched once the entityId is known; until then the flag reads false
 * (the required fail-safe default). Membership is checked against the loaded list.
 *
 * @param {string} featurePath The feature flag path/name to check.
 * @param {string} [entityId] The entity (facility) id to load flags for.
 * @returns {{ enabled: boolean, isLoaded: boolean }} The flag value for the
 *   supplied entity and whether that entity's flag list has finished loading.
 *   `isLoaded` lets callers defer capturing a stable render mode until the flag
 *   list is available (avoiding a premature false).
 */
const useFeatureFlagForEntity = (featurePath, entityId) => {
	const dispatch = useDispatch();

	const enabled = !!useSelector(
		state => state.featureFlags?.flags?.includes(featurePath)
	);

	const isLoaded = useSelector(
		state => state.featureFlags?.flags !== undefined
	);

	const needsReload = useSelector(
		state => !state.featureFlags?.isLoading && !state.featureFlags?.flags
	);

	useEffect(() => {
		if (entityId && needsReload) {
			dispatch(fetchFlags(entityId));
		}
	}, [entityId, needsReload, dispatch]);

	return { enabled, isLoaded };
};

const useFeatureFlags = () => {
	const dispatch = useDispatch();
	const useCoreEntitySlice = useCoreEntityContext();

	const facilityID = useSelector(
		(state) =>useCoreEntitySlice ? state.coreEntities?.ContextID :  state.entities?.ContextID,
		shallowEqual
	);
	const entityFlags = useSelector(
	state => state.featureFlags?.flags,
	shallowEqual
	);

	const needsReload = useSelector(
		state => !state.featureFlags?.isLoading && !state.featureFlags
	);

	useEffect(() => {
		if (needsReload) {
			dispatch(fetchFlags(facilityID));
		}
	}, [needsReload])

	return entityFlags;
}

export { useFeatureFlag, useFeatureFlagForEntity, useFeatureFlags };